import { zodResolver } from '@hookform/resolvers/zod';
import { CrmTaskStatus } from '@plato/data/prisma';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { LinkText } from '../../../components/ui/link-text.tsx';
import { useToast } from '../../../components/ui/use-toast.tsx';
import { useVoiceRecording } from '../../../lib/contexts/voice-recording-context.tsx';
import { useAppState } from '../../../lib/hooks/use-app-state.ts';
import { useDiscardChangesWarning } from '../../../lib/hooks/use-discard-changes-warning.ts';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { plainTextToLexicalSync } from '../../../lib/rich-text/plain-text-to-lexical.ts';
import type { CrmTaskDTO } from '../../../server/dto/crm-task-dto.ts';
import type { CrmTaskInputDTO } from '../../../server/dto/crm-task-input-dto.ts';
import { crmTaskInputDtoSchema } from '../../../server/dto/crm-task-input-dto.ts';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { TaskCreatedSource } from '../../analytics/types/analytics-events.ts';
import { DialogForm } from '../../dialog/dialog-form.tsx';
import { DialogStateManager, useDialog } from '../../dialog/dialog-provider.tsx';
import { Dialog } from '../../dialog/dialog.tsx';
import { useDrawers } from '../../drawers/all-drawers.tsx';
import { TaskAttachmentsField } from './task-attachments-field.tsx';
import { TaskDescriptionField } from './task-description-field.tsx';
import { TaskFormFields } from './task-form-fields.tsx';

type TaskCreateDialogProps = {
    onClose?: () => void;
    taskId?: string;
    links?: Pick<CrmTaskDTO['links'][0], 'modelId' | 'modelType' | 'title'>[];
    description?: string;
    assignedToId?: string;
    source: TaskCreatedSource;
};

export const TaskCreateDialog = DialogStateManager.create<TaskCreateDialogProps>(
    ({ links, onClose, description, assignedToId, source }: TaskCreateDialogProps) => {
        const { visible, remove } = useDialog();
        const appState = useAppState();
        const sendEvent = useAnalytics();
        const { t } = useLocale('tasks');
        const { toast } = useToast();
        const dialog = useDialog();
        const apiUtils = api.useUtils();
        const { currentSessionId } = useVoiceRecording();
        const { openTaskDetails } = useDrawers();

        const defaultValues = useMemo(() => {
            return {
                dueDate: new Date(),
                links: links || [],
                assignedToId: assignedToId ?? appState.platoUser.id,
                assignedById: appState.platoUser.id || '',
                createdById: appState.platoUser.id || '',
                status: CrmTaskStatus.OPEN,
                description: description ? (plainTextToLexicalSync(description) ?? undefined) : undefined,
                attachments: [],
            };
        }, [appState.platoUser.id, assignedToId, links, description]);

        const form = useForm<CrmTaskInputDTO>({
            defaultValues,
            resolver: zodResolver(crmTaskInputDtoSchema),
        });

        const {
            handleSubmit,
            formState: { isValid: isFormValid, isDirty },
        } = form;

        const { confirmDiscard } = useDiscardChangesWarning(isDirty);

        const handleClose = () => {
            confirmDiscard(() => {
                remove();
                onClose?.();
            });
        };

        const createTaskMutation = api.platoUser.command.tasks.postTasksActions.create.useMutation({
            onSuccess(data: CrmTaskDTO) {
                void Promise.all([
                    apiUtils.platoUser.query.tasks.getManyTasks.getListWithFilters.invalidate(),
                    apiUtils.platoUser.query.tasks.getManyTasks.getUpcomingCustomerTasks.invalidate(),
                    apiUtils.platoUser.query.tasks.getManyTasks.count.invalidate(),
                    apiUtils.platoUser.getManyCustomers.getListWithFilters.invalidate(),
                    apiUtils.platoUser.getManyQuotesOrders.getWithFilters.invalidate(),
                ]);

                if (data.dueDate) {
                    sendEvent('task_created', {
                        task_id: data.taskId,
                        due_date: data.dueDate.toISOString(),
                        assignee_id: data.assignedTo.id,
                        assignor_id: data.assignedBy.id,
                        is_assigned_to_oneself: data.assignedTo.id
                            ? data.assignedTo.id === data.assignedBy.id
                            : false,
                        related_entity_type: data.links[0]?.modelType,
                        related_entity_id: data.links[0]?.modelId,
                        source,
                    });
                }

                toast({
                    icon: 'success',
                    title: t('tasks__toast__created_success_title'),
                    createdLink: (
                        <LinkText
                            as="button"
                            onClick={() => {
                                openTaskDetails(data.taskId);
                            }}
                        />
                    ),
                });

                dialog.remove();
            },
            onError(error) {
                toast({
                    icon: 'warning',
                    title: 'Error',
                    description: error.message,
                });
            },
        });

        const onSubmit = (values: CrmTaskInputDTO) => {
            createTaskMutation.mutate(values);
        };

        return (
            <Dialog showClose open={visible} onClose={handleClose}>
                <DialogForm>
                    <DialogForm.Header title={t('tasks__form_create_title')} />
                    <DialogForm.Content>
                        <DialogForm.ItemList>
                            <TaskDescriptionField form={form} />
                        </DialogForm.ItemList>
                        <DialogForm.Group>
                            <TaskFormFields form={form} />
                            <TaskAttachmentsField form={form} />
                        </DialogForm.Group>
                    </DialogForm.Content>
                    <DialogForm.SubmitBlock
                        isLoading={createTaskMutation.isLoading}
                        onCancel={handleClose}
                        onSubmit={handleSubmit(onSubmit)}
                        disabled={!isFormValid || !!currentSessionId}
                    />
                </DialogForm>
            </Dialog>
        );
    },
);
